import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {NotificationTrigger} from "crqlar-react-common/dist/models/sector/sector.model";

export interface NotificationFeedback {
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
}

type Translate = (key: string) => string;

/** Turns the relay's HTTP status into the snackbar staff should see. */
export const getSendGridSnackbarProps = (
    t: Translate,
    statusCode: number | null | undefined
): NotificationFeedback => {
    if (statusCode === null || statusCode === undefined) {
        return {message: t('Email notification queued for delivery'), variant: 'info'}
    }
    if (statusCode >= 200 && statusCode < 300) {
        return {message: t('Email notification sent successfully'), variant: 'success'}
    }
    if (statusCode === 400) {
        return {message: t('Email could not be sent – please check the email address and content'), variant: 'error'}
    }
    if (statusCode === 401) {
        return {message: t('Email service is not properly configured – API key may be invalid'), variant: 'error'}
    }
    if (statusCode === 403) {
        return {message: t('Email sending not authorized – sender domain may not be verified'), variant: 'warning'}
    }
    if (statusCode === 413) {
        return {message: t('Email could not be sent – attachment is too large'), variant: 'error'}
    }
    if (statusCode === 429) {
        return {message: t('Email sending limit reached – please try again later'), variant: 'warning'}
    }
    return {message: t('Email service is temporarily unavailable – please try again later'), variant: 'error'}
}

/**
 * Sends one transactional reservation email now and reports what happened.
 *
 * `POST /v3/reservation/resendNotification` calls the relay directly and returns the status, unlike
 * the `allowNotify=true` flag on the update endpoint, which only queues a SCHEDULED correspondence
 * message (dispatched later by the light-worker) and tells the caller nothing. It also has no
 * group-member suppression, so it is the only way to mail the guest about a linked reservation.
 */
export const sendReservationNotification = async (
    reservationId: string,
    trigger: NotificationTrigger,
    t: Translate,
): Promise<NotificationFeedback> => {
    try {
        const response = (await ReservationService.resendNotification(reservationId, trigger)).data as {
            result: string;
            statusCode?: number | null;
        };

        if (response.result === 'no_template') {
            return {message: t('No email template configured for this event'), variant: 'error'};
        }
        if (response.result === 'error') {
            return {message: t('Email notification could not be sent'), variant: 'error'};
        }

        return getSendGridSnackbarProps(t, response.statusCode);
    } catch (error) {
        console.error(error);
        return {message: t('Email notification could not be sent'), variant: 'error'};
    }
};
